import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "@/App";
import { Zap, ArrowRight, Loader2, Eye, EyeOff } from "lucide-react";
import Logo from "../components/Logo";

export default function Register() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register, isAuthenticated, user } = useAuth();

  // Pass-through for ?next=checkout — set when the visitor clicked a paid
  // CTA on the landing page. Threaded through register → verify → billing
  // so the user lands on Paddle checkout instead of a generic dashboard.
  const nextParam = searchParams.get("next");
  const nextSuffix = nextParam ? `?next=${encodeURIComponent(nextParam)}` : "";

  // Redirect if already authenticated. Respect ?next=checkout for users who
  // are mid-purchase but already have an account.
  useEffect(() => {
    if (isAuthenticated && user) {
      if (nextParam === "checkout" && user.plan !== "pro") {
        navigate("/app/checkout", { replace: true });
      } else {
        navigate("/app/home", { replace: true });
      }
    }
  }, [isAuthenticated, user, navigate, nextParam]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      const result = await register(name, email, password);
      if (result.success) {
        if (window.fbq) window.fbq('track', 'CompleteRegistration');
        // "verify-after-pay" funnel — when the user arrived from a paid CTA
        // (?next=checkout), we skip the verify-email gate and send them
        // straight to Paddle checkout. The verification email is still sent
        // in the background; they'll see a soft banner on the dashboard
        // after they finish paying. This shaves one step off the funnel and
        // historically lifts paid conversion 8-15%.
        if (nextParam === "checkout") {
          navigate("/app/checkout", { replace: true });
        } else {
          navigate(`/verify-email${nextSuffix}`, { replace: true });
        }
      } else {
        setLoading(false);
        setError(result.error);
      }
    } catch (err) {
      setLoading(false);
      setError("Something went wrong. Please try again.");
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#09090b", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start", padding: "48px 24px" }}>
      <Helmet>
        <title>Sign Up — ChillRefer | Land Warm Referrals on Autopilot</title>
        <meta name="description" content="Create your ChillRefer account and start landing warm referrals at Google, Stripe, Meta, Amazon and more. AI finds referrers, sends outreach, runs follow-ups. $99/mo." />
        <meta name="keywords" content="sign up chillrefer, register chillrefer, referral automation signup, linkedin referral tool signup, chillrefer account, get referred to google, stripe referral, meta referral" />
        <link rel="canonical" href="https://chillrefer.com/register" />
        <meta property="og:title" content="Sign Up — ChillRefer | Land Warm Referrals on Autopilot" />
        <meta property="og:description" content="Create your ChillRefer account and start landing warm referrals at top tech companies. AI-powered LinkedIn outreach + Advocate Kits." />
        <meta property="og:url" content="https://chillrefer.com/register" />
        <meta property="og:type" content="website" />
      </Helmet>
      <div style={{ width: "100%", maxWidth: "400px" }}>
        <div style={{ textAlign: "center", marginBottom: "36px" }}>
          <Link to="/" style={{ display: "inline-flex", alignItems: "center", gap: "8px", textDecoration: "none", color: "#fff", marginBottom: "24px" }} data-testid="register-logo">
            <Logo size="md" />
          </Link>
          <h1 style={{ fontSize: "1.6rem", fontWeight: 700, marginTop: "20px", color: "#fff" }}>Create your account</h1>
          <p style={{ color: "#a1a1aa", fontSize: "0.88rem", marginTop: "6px" }}>Start getting referred to your dream jobs</p>
        </div>

        <form onSubmit={handleSubmit} data-testid="register-form">
          {error && (
            <div data-testid="register-error" style={{ padding: "10px 14px", marginBottom: "16px", background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: "4px", color: "#f87171", fontSize: "0.82rem" }}>
              {error}
            </div>
          )}

          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", fontSize: "0.75rem", fontWeight: 500, color: "#a1a1aa", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>Full Name</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} required data-testid="register-name" style={{ width: "100%", padding: "12px 14px", background: "#09090b", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "4px", color: "#fff", fontSize: "0.9rem", outline: "none" }} placeholder="John Doe" />
          </div>

          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", fontSize: "0.75rem", fontWeight: 500, color: "#a1a1aa", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="register-email" style={{ width: "100%", padding: "12px 14px", background: "#09090b", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "4px", color: "#fff", fontSize: "0.9rem", outline: "none" }} placeholder="you@company.com" />
          </div>

          <div style={{ marginBottom: "24px" }}>
            <label style={{ display: "block", fontSize: "0.75rem", fontWeight: 500, color: "#a1a1aa", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>Password</label>
            <div style={{ position: "relative" }}>
              <input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="register-password" style={{ width: "100%", padding: "12px 42px 12px 14px", background: "#09090b", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "4px", color: "#fff", fontSize: "0.9rem", outline: "none" }} placeholder="Min 6 characters" />
              <button type="button" onClick={() => setShowPw(!showPw)} style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#52525b", cursor: "pointer" }}>
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <button type="submit" disabled={loading} data-testid="register-submit-btn" style={{ width: "100%", padding: "13px", background: "#FACC15", color: "#000", border: "none", fontWeight: 700, fontSize: "0.9rem", cursor: loading ? "not-allowed" : "pointer", borderRadius: "2px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", opacity: loading ? 0.7 : 1 }}>
            {loading ? <Loader2 size={16} className="animate-spin" /> : <>Create Account <ArrowRight size={16} /></>}
          </button>
        </form>

        <p style={{ textAlign: "center", marginTop: "20px", color: "#52525b", fontSize: "0.85rem" }}>
          Already have an account?{" "}
          <Link to="/login" style={{ color: "#FACC15", textDecoration: "none", fontWeight: 600 }} data-testid="register-login-link">Sign in</Link>
        </p>
      </div>

      {/* SEO content block — helps this page rank for signup intent queries,
          brings the word count over 300 for indexing quality signals, and
          restates the value prop for anyone who scrolls past the form. */}
      <section style={{ maxWidth: "620px", margin: "48px auto 0", padding: "0 8px", color: "#a1a1aa", fontSize: "0.88rem", lineHeight: 1.7 }}>
        <h2 style={{ color: "#fff", fontSize: "1.15rem", fontWeight: 700, marginBottom: "10px" }}>Start landing warm referrals in under 2 minutes</h2>
        <p style={{ marginBottom: "14px" }}>
          Cold-applying on LinkedIn has a 1-2% callback rate. Referred candidates are 5-7x more likely to be hired.
          ChillRefer automates the entire path from "no contacts at your target company" to "an employee just referred you internally" —
          without you writing a single message or spending hours stalking profiles.
        </p>
        <p style={{ marginBottom: "14px" }}>
          After you sign up, ChillRefer connects to your LinkedIn (via the official Unipile OAuth — we never see your password),
          reads your resume, and starts finding the top 2-5 people most likely to refer you at each company you care about —
          Google, Stripe, Meta, Amazon, Anthropic, OpenAI, Netflix, and hundreds more. The AI writes each outreach message
          personally from your background and the recipient's profile, then runs a 5-touchpoint follow-up sequence spaced 24-72
          hours apart. Every reply is auto-classified as positive, engagement, or dead so you know exactly who's interested.
        </p>
        <h3 style={{ color: "#fff", fontSize: "1rem", fontWeight: 700, marginTop: "22px", marginBottom: "8px" }}>What's included in Pro ($99/month)</h3>
        <ul style={{ listStyle: "disc", paddingLeft: "22px", marginBottom: "16px" }}>
          <li style={{ marginBottom: "6px" }}>Autopilot job discovery + auto-outreach to hiring managers at your target companies</li>
          <li style={{ marginBottom: "6px" }}>AI-generated referral messages personalised from your resume + each recipient's LinkedIn</li>
          <li style={{ marginBottom: "6px" }}>5-step drip sequence with smart reply classification (positive / engaging / dead)</li>
          <li style={{ marginBottom: "6px" }}>Advocate Kit — a one-link referral page any employee can forward to their hiring manager</li>
          <li style={{ marginBottom: "6px" }}>Auto-withdraw stale LinkedIn invites so your account stays safe</li>
          <li style={{ marginBottom: "6px" }}>Job Kanban, Network Kanban, resume ATS score, and AI Copilot chat</li>
        </ul>
        <h3 style={{ color: "#fff", fontSize: "1rem", fontWeight: 700, marginTop: "22px", marginBottom: "8px" }}>Outcome Guarantee</h3>
        <p style={{ marginBottom: "14px" }}>
          If you don't land 5 internal referral conversations in your first 30 days on Pro, we keep your account active for
          free until you do. No hoops, no arguments — just email support and we extend your access.
          Cancel anytime in one click from your billing page.
        </p>
        <p style={{ fontSize: "0.82rem", color: "#71717a" }}>
          By creating an account you agree to the <Link to="/terms" style={{ color: "#a1a1aa", textDecoration: "underline" }}>Terms</Link> and{" "}
          <Link to="/privacy" style={{ color: "#a1a1aa", textDecoration: "underline" }}>Privacy Policy</Link>.
          Questions? Read the <Link to="/refund" style={{ color: "#a1a1aa", textDecoration: "underline" }}>Outcome Guarantee</Link> details.
        </p>
      </section>
    </div>
  );
}
